import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useHasGate } from 'academy-common-lib/features/accounts/accountsQueries';
export const LAYOUT_STORAGE_KEY = 'academy-ai-assistant-layout';

/**
 * How the assistant is currently laid out on the page.
 * - `closed`     — not shown
 * - `sidebar`    — 400px panel docked to the left of the page content
 * - `fullscreen` — fills the page content region (below header, right of nav rail)
 */

/** The modes in which the assistant is visible. */

export const useAssistantLayout = create()(persist(set => ({
  mode: 'closed',
  lastConversationId: null,
  open: (mode = 'sidebar') => set({
    mode
  }),
  close: () => set({
    mode: 'closed'
  }),
  setMode: mode => set({
    mode
  }),
  toggleMode: () => set(state => ({
    mode: state.mode === 'sidebar' ? 'fullscreen' : 'sidebar'
  })),
  setLastConversationId: id => set({
    lastConversationId: id
  })
}), {
  name: LAYOUT_STORAGE_KEY,
  // Only the mode and last conversation are worth persisting; actions are
  // recreated on load.
  partialize: state => ({
    mode: state.mode,
    lastConversationId: state.lastConversationId
  })
}));

/**
 * Whether this page load is a genuine refresh (F5 / reload) rather than a fresh
 * navigation to a new page. Uses the Navigation Timing API; anything we can't
 * positively identify as a reload is treated as a navigation.
 */
const isPageReload = () => {
  try {
    const [entry] = performance.getEntriesByType('navigation');
    return (entry === null || entry === void 0 ? void 0 : entry.type) === 'reload';
  } catch (_unused) {
    return false;
  }
};

// Fullscreen is only sticky across a refresh. Landing on a new Academy page
// (any non-reload navigation) must not reopen fullscreen over the page, so a
// restored fullscreen is demoted to the sidebar. Runs once at module load —
// hydration is synchronous, so this settles before React reads the store (no
// flash) — and `setMode` persists it so a later refresh of the new page keeps
// the sidebar too. Relies on each Academy page being its own document load,
// the same premise the persisted `mode` restore already depends on.
if (useAssistantLayout.getState().mode === 'fullscreen' && !isPageReload()) {
  useAssistantLayout.getState().setMode('sidebar');
}

/** True when the assistant is visible in any mode. */
export const selectIsOpen = mode => mode !== 'closed';

/**
 * Resolve the effective mode for the current device and permissions. The
 * sidebar is only offered when the viewport can host it (not mobile) and the
 * account is ungated for the assistant; otherwise an open assistant is always
 * fullscreen. Kept pure (no hooks) so it can be reused and reasoned about
 * without a React render.
 */
export const resolveLayoutMode = (mode, {
  isMobile,
  canUseSidebar
}) => {
  if (mode === 'sidebar' && (isMobile || !canUseSidebar)) return 'fullscreen';
  return mode;
};

/**
 * The mode the assistant should actually render in right now — the stored mode
 * with the mobile and gating rules applied. Reads the media query and gate
 * itself so callers (the overlay, action buttons, harness) never have to thread
 * them through. The sidebar requires a confirmed gate, so while it is unknown
 * (loading) the assistant stays fullscreen rather than flashing the sidebar.
 */
export const useEffectiveAssistantMode = () => {
  const mode = useAssistantLayout(state => state.mode);
  const {
    isMobile
  } = useMediaQuery();
  const {
    hasGate
  } = useHasGate('Academy:AiAssistant:AskAcademyAssistant');
  return resolveLayoutMode(mode, {
    isMobile,
    canUseSidebar: hasGate === true
  });
};